<template>
  <ChangeCommissionLevelModal :editedBoost="editedBoost" :isDeleting="isDeleting" />
  <div
    v-if="selectedDates.size === 0 && !isEmptySelection"
    class="tw:px-3 tw:h-[calc(100%-49px)] tw:flex tw:items-center tw:w-full"
  >
    <p class="tw:font-bold tw:text-center">
      {{ $t('rc_smartboost_prompt') }}
    </p>
  </div>
  <div
    v-else-if="selectedDates.size === 0 && isEmptySelection"
    class="tw:p-3 tw:h-[calc(100%-49px)] tw:flex tw:items-start tw:w-full"
  >
    <ul class="tw:gap-y-2 tw:flex tw:flex-col tw:w-full">
      <li class="tw:w-full tw:flex tw:flex-row tw:gap-x-1">
        <span class="tw:font-bold tw:bg-gray-100 tw:rounded-lg tw:p-4 tw:flex-1 tw:text-xs tw:lg:text-sm">
          {{ 'DD.MM.YYYY' }}</span
        >
      </li>
    </ul>
  </div>
  <div
    v-else-if="!isEditing"
    class="tw:p-3 tw:h-[calc(100%-49px)] tw:flex tw:items-start tw:w-full tw:max-h-[] tw:overflow-auto"
  >
    <ul class="tw:gap-y-2 tw:flex tw:flex-col tw:w-full">
      <li
        v-for="(date, index) in getSelectedDatesFormatted"
        :key="`${index}_date`"
        class="tw:w-full tw:flex tw:flex-row tw:gap-x-1"
      >
        <span class="tw:font-bold tw:text-xs tw:lg:text-sm tw:bg-gray-100 tw:rounded-lg tw:p-4 tw:flex-1">
          {{ date.formattedDate }}</span
        >
        <button class="tw:px-3 tw:hover:cursor-pointer" @click="removeRange(date.timestamps)">
          <Trash class="tw:size-[18px]" />
        </button>
      </li>
    </ul>
  </div>
  <div
    v-else-if="isEditing"
    class="tw:p-3 tw:h-[calc(100%-49px)] tw:flex tw:items-start tw:w-full tw:md:max-h-[420px] tw:lg:max-h-[330px] tw:xl:max-h-[378px] tw:overflow-auto"
  >
    <ul class="tw:gap-y-2 tw:flex tw:flex-col tw:w-full">
      <li
        v-for="(date, index) in getSelectedBoostsFormatted"
        :key="`${index}_date`"
        class="tw:w-full tw:flex tw:flex-row tw:gap-x-1"
      >
        <span class="tw:font-bold tw:text-xs tw:lg:text-sm tw:bg-gray-100 tw:rounded-lg tw:p-4 tw:flex-1 tw:mr-4">
          {{ date.formattedDate }}</span
        >
        <div
          class="tw:border tw:border-gray-300 tw:rounded-lg tw:flex tw:items-center tw:px-4 tw:relative"
          :data-testid="`smart-boost-commission-dropdown-${index}`"
          @click="openList(date.id)"
        >
          <div class="tw:flex tw:items-center tw:gap-x-3 tw:text-sm tw:hover:cursor-pointer">
            {{ findCommissionLevelByTimestamps(date.timestamps) }}
            <svg
              width="12"
              height="7"
              viewBox="0 0 12 7"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              :class="{ 'tw:rotate-180': isListOpen === date.id }"
            >
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M11.707 0.293C11.512 0.098 11.256 0 11 0C10.744 0 10.488 0.098 10.293 0.293L6.00012 4.586L1.70721 0.293C1.51222 0.098 1.25622 0 1.00023 0C0.744234 0 0.48824 0.098 0.293244 0.293C-0.097748 0.684 -0.097748 1.317 0.293244 1.707L5.29314 6.707C5.48814 6.902 5.74413 7 6.00012 7C6.25512 7 6.51111 6.902 6.70711 6.707L11.707 1.707C11.902 1.512 12 1.256 12 1C12 0.744 11.902 0.488 11.707 0.293Z"
                fill="#171717"
              />
            </svg>
          </div>

          <div
            v-click-outside="closeList"
            class="tw:absolute tw:bg-white tw:h-auto tw:z-10 tw:left-[-1px] tw:right-[-1px] tw:top-[52px] tw:lg:top-[56px] tw:shadow tw:rounded-lg tw:border tw:border-gray-100"
            v-if="isListOpen && isListOpen === date.id"
          >
            <ul>
              <li
                v-for="level in levelsToShow"
                :key="`boost-level-${level}`"
                class="tw:p-4 tw:hover:cursor-pointer tw:hover:bg-blue-200 tw:hover:text-blue-700 tw:first:rounded-t-lg tw:last:rounded-b-lg tw:text-sm"
                :data-testid="`smart-boost-edit-commission-option-${level}`"
                @click="chooseNewCommission(date, level)"
                :class="
                  Number(date.currentCommissionLevel) === Number(level)
                    ? 'tw:bg-blue-200 tw:text-blue-700'
                    : 'tw-bg-white'
                "
              >
                {{ formatPercentage(language, level, 0, 0) }}
              </li>
            </ul>
          </div>
        </div>
        <button class="tw:px-4 tw:hover:cursor-pointer" :data-testid="`smart-boost-delete-boost-${index}`" @click="startDeleting(date)">
          <Trash class="tw:size-[18px]" />
        </button>
      </li>
    </ul>
  </div>
</template>
<script setup>
import { computed, ref } from 'vue'

import Trash from '@/components/icons/Trash.svg?component'
import ChangeCommissionLevelModal from '@/components/rateConnect/smartBoost/ChangeCommissionLevelModal.vue'

import { useSmartBoost } from '@/components/rateConnect/smartBoost/useSmartBoost'
import { formatPercentage } from '@/utils/Utility.ts'
import { useActiveBoosts, useBoostLevels, useCampaign } from '../queries'
import { selectedAccommodationId } from '@/layouts/queries'

import appAnalytics from '@/utils/tracking'
import { useSessionValues } from '@/composables/useSessionValues'

const { selectedDates, removeRange, isEmptySelection, getSelectedDatesFormatted, formatDateRangesByBoost } =
  useSmartBoost()
const { activeBoosts } = useActiveBoosts(selectedAccommodationId)

const { language } = useSessionValues()
const isListOpen = ref(null)
const isDeleting = ref(false)
defineProps({
  isEditing: {
    type: Boolean,
    default: false,
  },
})

const openList = id => {
  isListOpen.value = id
}
const closeList = () => {
  isListOpen.value = null
}
const { selectCommission, openChangeCommissionLevelModal } = useSmartBoost()
const { boostLevels } = useBoostLevels()
const { currentCommissionLevel } = useCampaign(selectedAccommodationId)
const editedBoost = ref(null)

const levelsToShow = computed(() => boostLevels.value?.filter(level => level > currentCommissionLevel.value))
const chooseNewCommission = (date, level) => {
  if (Number(date.currentCommissionLevel) === Number(level)) {
    closeList()
    return
  }

  appAnalytics.track(appAnalytics.events.RC_SMART_BOOST_EDIT_COMMISSION, {
    item_id: selectedAccommodationId.value,
    new_commission_level: level,
  })
  selectCommission(level)
  isDeleting.value = false
  editedBoost.value = date
  openChangeCommissionLevelModal()
  closeList()
}

const startDeleting = date => {
  appAnalytics.track(appAnalytics.events.RC_SMART_BOOST_DELETE_ICON_CLICKED, {
    item_id: selectedAccommodationId.value,
  })
  isDeleting.value = true
  editedBoost.value = date
  openChangeCommissionLevelModal()
}

const getSelectedBoostsFormatted = computed(() => {
  return formatDateRangesByBoost(Array.from(selectedDates.value), language.value, activeBoosts.value)
})

const findCommissionLevelByTimestamps = timestamps => {
  if (!activeBoosts.value || !timestamps) return null

  for (const [commissionLevel, boosts] of Object.entries(activeBoosts.value)) {
    for (const boost of boosts) {
      const boostTimestamps = []
      const startDate = new Date(boost.startDate + 'T00:00:00')
      const endDate = new Date(boost.endDate + 'T00:00:00')

      const currentDate = new Date(startDate)
      while (currentDate <= endDate) {
        boostTimestamps.push(currentDate.getTime())
        currentDate.setDate(currentDate.getDate() + 1)
      }

      const allMatch = timestamps.every(ts => {
        return boostTimestamps.includes(ts)
      })
      const sameLength = timestamps.length === boostTimestamps.length

      if (allMatch && sameLength) {
        return formatPercentage(language.value, commissionLevel, 0, 0)
      }
    }
  }

  return null
}
</script>
